/*
 * @providesModule NotificationNavigationController
 *
 */

import React from 'react';

import {
  Animated,
  Easing,
  GestureResponderEvent,
  PanResponder,
  PanResponderGestureState,
  PanResponderInstance,
} from 'react-native';

// Exported for testing ONLY. Seriously. Do NOT touch these if your filename isn't NotificationNavigationController-test.js
export const TESTING_ONLY_getInstance = () => notifNavigationControllerInstance;
export const TESTING_ONLY_setInstance = (instance: NotificationNavigationController | null) =>
  (notifNavigationControllerInstance = instance);

let notifNavigationControllerInstance: NotificationNavigationController | null = null;

export type ToastDisplayProps = {
  // how long in milliseconds the toast should be displayed before hiding itself automatically.
  // Falsy values (including 0) will live until dismissed by an outside force. Default undefined.
  lifetime?: number | null;
  // Determines if toast to display should enter with a translate down animation. Default false.
  animated?: boolean;
  // Determines if swipe gesture up to dismiss is enabled. Default false.
  swipeToDismissEnabled?: boolean;
  // Callback that fires when toast is dismissed by swipe gesture, another toast being displayed, or expiring. Default () => {}.
  onHide?: () => void;
};

type Props = {};
type State = {
  notifComponent: React.ReactElement | null;
  notifDisplayProps: ToastDisplayProps;
  entranceAnimationProgress: Animated.Value;
  panResponderAnimation: Animated.Value;
};

const SKZ_TOAST_HEIGHT = 60;
const SAFE_MODAL_CHANGE_TIME = 300;
const TRANSFORM_INTERPOLATION = {
  inputRange: [0, 1],
  outputRange: ['-100%', '0%'],
};

export default class NotificationNavigationController extends React.Component<Props, State> {
  panResponder: PanResponderInstance | null;
  expirationTimer: NodeJS.Timeout | null;

  constructor(props: Props) {
    super(props);
    this.state = {
      notifComponent: null,
      notifDisplayProps: this._setupDisplayProps(),
      entranceAnimationProgress: new Animated.Value(0),
      panResponderAnimation: new Animated.Value(0),
    };

    this.panResponder = PanResponder.create({
      onStartShouldSetPanResponder: this._handleStartShouldSetPanResponder,
      onPanResponderMove: this._handlePanResponderMove,
      onPanResponderRelease: this._handlePanResponderRelease,
      onMoveShouldSetPanResponderCapture: this._handleMoveShouldSetResponderCapture,
    });

    notifNavigationControllerInstance = this;
    this.expirationTimer = null;
  }

  static showInAppToast(notif: React.ReactElement, displayProps?: ToastDisplayProps | null) {
    notifNavigationControllerInstance?._showInAppToast(notif, displayProps);
  }

  static hideInAppToast(completion?: () => void) {
    notifNavigationControllerInstance?._hideInAppToast(completion);
  }

  // As soon as the user moves touch, steal the responder away from any touchables inside the notification child view
  _handleMoveShouldSetResponderCapture = (
    _event: GestureResponderEvent,
    gestureState: PanResponderGestureState
  ): boolean => {
    return gestureState.dy < -1; // eslint-disable-line no-magic-numbers
  };

  _handleStartShouldSetPanResponder = (
    _event: GestureResponderEvent,
    _gestureState: PanResponderGestureState
  ): boolean => {
    return true;
  };

  _handlePanResponderRelease = (
    _event: GestureResponderEvent,
    gestureState: PanResponderGestureState
  ) => {
    const shouldDismiss = this._shouldDismissForPanResponderRelease(gestureState);
    if (shouldDismiss) {
      this._hideInAppToast();
    } else {
      this._animateSpring(0);
    }
  };

  _handlePanResponderMove = (
    _event: GestureResponderEvent,
    gestureState: PanResponderGestureState
  ) => {
    this.state.panResponderAnimation.setValue(Math.min(0, 0 + gestureState.dy));
  };

  _shouldDismissForPanResponderRelease(gestureState: PanResponderGestureState): boolean {
    const isUp = gestureState.dy < 0;
    const isMoving = gestureState.vy < 0;

    return isUp && isMoving;
  }

  _setupDisplayProps(displayProps?: ToastDisplayProps | null): ToastDisplayProps {
    const props: ToastDisplayProps = {};
    props.animated = displayProps?.animated ?? false;
    props.lifetime = displayProps?.lifetime;
    props.swipeToDismissEnabled = displayProps?.swipeToDismissEnabled ?? false;
    props.onHide =
      displayProps?.onHide ??
      (() => {
        /* */
      });
    return props;
  }

  _setExpirationTimer(lifetime?: number | null) {
    // expiration timer should already be clear, because we are supposed to have hidden the previous
    // toast and cleared the timer already, but just in case it's not, let's double check it
    if (this.expirationTimer) {
      this._clearExpirationTimer();
    }

    if (!lifetime) {
      return;
    }

    this.expirationTimer = setTimeout(() => {
      this._hideInAppToast();
    }, lifetime);
  }

  _clearExpirationTimer() {
    if (!this.expirationTimer) {
      return;
    }

    clearTimeout(this.expirationTimer);
    this.expirationTimer = null;
  }

  _showInAppToast(notif: React.ReactElement, displayProps?: ToastDisplayProps | null) {
    NotificationNavigationController.hideInAppToast(() => {
      setTimeout(() => {
        this.setState(
          {
            notifComponent: notif,
            notifDisplayProps: this._setupDisplayProps(displayProps),
          },
          () => {
            if (this.state.notifDisplayProps.animated) {
              this._animateEntry(1);
            } else {
              this.state.entranceAnimationProgress.setValue(1);
            }
            this._setExpirationTimer(this.state.notifDisplayProps.lifetime);
          }
        );
      }, SAFE_MODAL_CHANGE_TIME);
    });
  }

  _hideInAppToast(completion?: () => void) {
    const onHide = this.state.notifDisplayProps.onHide; // pull it out here so that the closure actually grabs the function and not `this.state` which is already cleared by the time `done` gets called
    const done = () => {
      onHide?.();
      completion?.();
    };
    if (this.state.notifDisplayProps.animated) {
      this._animateEntry(0, () => {
        this._removeToastFromView(done);
      });
    } else {
      this._removeToastFromView(done);
    }
  }

  _removeToastFromView(completion?: () => void) {
    this._clearExpirationTimer();
    this.setState({notifComponent: null, notifDisplayProps: this._setupDisplayProps({})}, () => {
      this.state.entranceAnimationProgress.setValue(0);
      this.state.panResponderAnimation.setValue(0);
      completion?.();
    });
  }

  _animateSpring(endValue: number) {
    Animated.spring(this.state.panResponderAnimation, {
      toValue: endValue,
      velocity: 3,
      tension: 2,
      friction: 6,
      useNativeDriver: false,
    }).start();
  }

  _animateEntry(endValue: number, completion?: () => void) {
    Animated.timing(this.state.entranceAnimationProgress, {
      toValue: endValue,
      duration: 750,
      easing: Easing.inOut(Easing.sin),
      useNativeDriver: false,
    }).start(() => {
      completion?.();
    });
  }

  render(): React.ReactElement {
    return (
      <Animated.View
        style={{
          position: 'absolute',
          top: this.state.entranceAnimationProgress.interpolate(TRANSFORM_INTERPOLATION),
          right: 0,
          left: 0,
          height: SKZ_TOAST_HEIGHT,
          zIndex: 15,
        }}
      >
        <Animated.View
          style={{flex: 1, alignSelf: 'stretch', top: this.state.panResponderAnimation}}
          {...(this.state.notifDisplayProps.swipeToDismissEnabled
            ? this.panResponder?.panHandlers || {}
            : {})}
        >
          {this.state.notifComponent}
        </Animated.View>
      </Animated.View>
    );
  }
}
